import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExampleImagesCarousel from '@shared/components/ExampleImagesCarousel';
import { useProxyUrls } from '@shared/hooks/useProxyUrl';

import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import { Box, Grid, Button, Stack } from '@mui/material';

import type { Game } from '@shared/types';

interface ExampleImagesSectionProps {
  gameDefaults?: Game;
}

const ExampleImagesSection: FC<ExampleImagesSectionProps> = ({ gameDefaults }) => {
  const { t } = useTranslation();
  
  const hasAnyImages = Boolean(
    (Array.isArray(gameDefaults?.startGameImageExampleUrl) ? gameDefaults?.startGameImageExampleUrl[0] : gameDefaults?.startGameImageExampleUrl) ||
    (Array.isArray(gameDefaults?.endGameImageExampleUrl) ? gameDefaults?.endGameImageExampleUrl[0] : gameDefaults?.endGameImageExampleUrl) ||
    (Array.isArray(gameDefaults?.gameTileExampleUrl) ? gameDefaults?.gameTileExampleUrl[0] : gameDefaults?.gameTileExampleUrl)
  );

  const videoList = (Array.isArray(gameDefaults?.video) ? gameDefaults?.video : (gameDefaults?.video ? [gameDefaults.video] : [])).filter(Boolean) as string[];
  const hasAnyVideo = videoList.length > 0;

  // Выносим вызов хука на верхний уровень
  const imageUrls = useProxyUrls([
    ...(Array.isArray(gameDefaults?.startGameImageExampleUrl)
      ? gameDefaults?.startGameImageExampleUrl
      : [gameDefaults?.startGameImageExampleUrl]),
    ...(Array.isArray(gameDefaults?.endGameImageExampleUrl)
      ? gameDefaults?.endGameImageExampleUrl
      : [gameDefaults?.endGameImageExampleUrl]),
    ...(Array.isArray(gameDefaults?.gameTileExampleUrl)
      ? gameDefaults?.gameTileExampleUrl
      : [gameDefaults?.gameTileExampleUrl]),
  ].filter(Boolean));

  // Ширина блока картинок, чтобы видео совпадало по ширине
  const imagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [imagesContentWidth, setImagesContentWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = imagesContainerRef.current;
    if (!element) return;
    const update = () => setImagesContentWidth(element.clientWidth);
    update();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(element);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [hasAnyImages, gameDefaults]);

  if (!gameDefaults || !(hasAnyImages || hasAnyVideo)) {
    return null;
  }

  return (
    <Box sx={{ mb: 4, mt: 3 }}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={12} lg={12}>
          <Stack spacing={2} alignItems="center">
            {hasAnyImages && (
              <Box ref={imagesContainerRef} sx={{ width: '110%', maxWidth: '1200px' }}>
                <ExampleImagesCarousel
                  imageUrls={imageUrls}
                />
              </Box>
            )}

            {hasAnyVideo && (
              <Box sx={{ width: '100%', mx: 'auto' }}>
                <Grid container spacing={0} justifyContent="center">
                  {videoList.map((rawUrl, index) => {
                    const url = typeof rawUrl === 'string' ? rawUrl.trim() : '';
                    const isYouTube = /youtube\.com|youtu\.be/.test(url);
                    const embedUrl = (() => {
                      if (!isYouTube) return url;
                      try {
                        const u = new URL(url.startsWith('http') ? url : `https://${url}`);
                        if (u.hostname.includes('youtu.be')) {
                          return `https://www.youtube.com/embed/${u.pathname.replace('/', '')}`;
                        }
                        const v = u.searchParams.get('v');
                        return v ? `https://www.youtube.com/embed/${v}` : url;
                      } catch {
                        return url;
                      }
                    })();
                    return (
                      <Grid item xs={12} sm={10} md={8} key={`${url}-${index}`}>
                        {isYouTube ? (
                          <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                            <iframe
                              src={embedUrl}
                              title={`video-${index}`}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              onError={(e) => {
                                console.error('Failed to load YouTube iframe:', e);
                                // Fallback: show link to video
                                const target = e.currentTarget.parentElement;
                                if (target) {
                                  target.innerHTML = `
                                    <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; border-radius: 8px;">
                                      <a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration: none; color: #1976d2; font-weight: 500;">
                                        📺 Смотреть видео на YouTube
                                      </a>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%' }}>
                            <video controls style={{ width: '100%', borderRadius: 8 }} src={embedUrl} />
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
            
            {/* Кнопка ПРИМЕР ИГРЫ */}
            {gameDefaults.demo && (
              <Button
                variant="contained"
                startIcon={<VideogameAssetIcon />}
                onClick={() => {
                  const url = gameDefaults.demo;
                  if (url) {
                    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
                    window.open(fullUrl, '_blank');
                  }
                }}
                sx={{
                  backgroundColor: '#80FFB3',
                  color: '#283757',
                  '&:hover': {
                    backgroundColor: '#6BFF9E',
                  },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(128, 255, 179, 0.3)',
                  '&:active': {
                    transform: 'translateY(1px)',
                    boxShadow: '0 2px 8px rgba(128, 255, 179, 0.3)',
                  }
                }}
              >
                {t('client.playExample')}
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExampleImagesSection; 